import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

/**
 * Where this patient stands across the whole childhood schedule (KIN-1811).
 *
 * This endpoint answers in the `ApiResponse` envelope, unlike the Healthlink ones which
 * return a bare DTO — but nothing here has to care: the shared axios instance already
 * unwraps `{status: "success", data}` in a response interceptor. Reading `response.data.data`
 * unwraps it twice and yields undefined, which renders as a silently empty screen.
 *
 * The backend does the judging — due dates run from the last dose actually given rather than
 * from date of birth, and slots the school programme delivers are marked rather than
 * assumed. Nothing here recomputes any of that.
 */
export const usePatientSchedule = (patientId) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["patientSchedule", tenantVal, patientId],
    enabled: !!tenantVal && !!patientId,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/immunisationschedule/patient/${patientId}`
      );

      return response.data ?? null;
    },
    meta: { errorMessage: "Error fetching the immunisation schedule" },
  });
};
